import { HttpErrorResponse } from '@angular/common/http';
import { toast, toastStore } from '@digitale-menschen/component-library-angular';
import type { ToastOptions } from '@digitale-menschen/component-library';

type NotifyVariant = 'error' | 'warning' | 'success' | 'info';

/** Variants that stay until the user dismisses them, so a failure can't disappear unread. */
const PERSISTENT_VARIANTS: readonly NotifyVariant[] = ['error', 'warning'];

/**
 * How long a toast carrying an `action` stays, in ms. Twice the toaster's 5s
 * default: an action has to be read, decided on and reached, which the default
 * leaves no room for.
 */
export const ACTIONABLE_TOAST_DURATION = 10_000;

/**
 * Identifies a toast by its content, so an identical message triggered again
 * while the first one is still on screen is suppressed instead of stacked.
 */
function buildContentId(variant: NotifyVariant, title: string, options: ToastOptions): string {
  return options.id ?? `${variant}::${title}::${options.description ?? ''}`;
}

function isOnScreen(id: string): boolean {
  return toastStore.getToasts().some((existingToast) => existingToast.id === id && !existingToast.dismissed);
}

function show(variant: NotifyVariant, title: string, options: ToastOptions = {}): string {
  const id = buildContentId(variant, title, options);

  if (isOnScreen(id)) {
    return id;
  }

  const resolvedOptions: ToastOptions = { ...options, id };

  if (PERSISTENT_VARIANTS.includes(variant) && resolvedOptions.duration === undefined) {
    resolvedOptions.duration = Number.POSITIVE_INFINITY;
    resolvedOptions.closeButton = resolvedOptions.closeButton ?? true;
  }

  return toast[variant](title, resolvedOptions);
}

/**
 * Application-wide toast facade over the design system's `toast()`.
 *
 * Adds two behaviours every call site wants: identical messages are shown only
 * once while still visible, and errors and warnings do not auto-dismiss.
 * Pass an explicit `duration` to opt out of the persistence.
 */
export const notify = {
  error: (title: string, options?: ToastOptions): string => show('error', title, options),
  warning: (title: string, options?: ToastOptions): string => show('warning', title, options),
  success: (title: string, options?: ToastOptions): string => show('success', title, options),
  info: (title: string, options?: ToastOptions): string => show('info', title, options),
  dismiss: (id?: string): void => toast.dismiss(id),
};

/**
 * The generic failure toast for a request thrown against, everywhere it applies.
 *
 * Prefers the server's own message over `fallbackDescription`, which itself falls back to
 * a generic wording — so a caller only needs `fallbackDescription` where it has something
 * more specific to say than «check your input and try again».
 *
 * @param {unknown} error - What the request rejected with
 * @param {string} [fallbackDescription] - Shown where the server gave no message of its own
 */
export function notifyRequestFailure(error: unknown, fallbackDescription?: string): void {
  const serverMessage = (error as HttpErrorResponse)?.error?.message;
  notify.error($localize`:@@common.errors.generic:Ein Fehler ist aufgetreten`, {
    description:
      serverMessage ??
      fallbackDescription ??
      $localize`:@@common.errors.genericDescription:Bitte überprüfen Sie Ihre Eingaben und versuchen Sie es erneut.`,
  });
}
